import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, ExternalLink } from "lucide-react";
import { getBooks, getAuthor } from "@/lib/api";
import BookCard from "@/components/BookCard";
import Newsletter from "@/components/Newsletter";

const HERO_BG = "https://images.unsplash.com/photo-1535905557558-afc4877a26fc";

export default function Home() {
  const [books, setBooks] = useState([]);
  const [author, setAuthor] = useState(null);

  useEffect(() => {
    getBooks().then(setBooks).catch(() => {});
    getAuthor().then(setAuthor).catch(() => {});
  }, []);

  const featured = books.find((b) => b.featured) || books[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center grain overflow-hidden">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-xs tracking-accent uppercase text-amber-500 mb-6">Supernatural thriller author</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight">
              The end of the world has a <span className="italic text-amber-500">witness</span>.
            </h1>
            <p className="mt-8 text-lg text-zinc-300 leading-relaxed max-w-xl">
              I'm A.R. Winfree, author of The Luminaria Chronicles. Supernatural thrillers about a hidden war between light and shadow, and the ordinary people caught in the middle.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/books"
                data-testid="hero-explore-books"
                className="group inline-flex items-center gap-2 bg-amber-500 text-zinc-950 font-bold text-sm tracking-wide px-7 py-4 hover:bg-amber-400 transition-colors"
              >
                Explore the books
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-link"
                className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-100 text-sm tracking-wide px-7 py-4 hover:border-amber-500 hover:text-amber-500 transition-colors"
              >
                Meet the author
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED / LATEST RELEASE */}
      {featured && (
        <section data-testid="featured-section" className="relative py-24 lg:py-32 border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative mx-auto lg:mx-0 max-w-sm w-full"
            >
              <div className="absolute -inset-4 bg-amber-500/10 blur-2xl" />
              <img src={featured.cover_image} alt={featured.title} className="relative w-full border border-zinc-800 shadow-2xl" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs tracking-accent uppercase text-amber-500 mb-4">Featured Release · {featured.release_year}</p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{featured.title}</h2>
              {featured.series && <p className="text-sm text-zinc-500 mt-2">{featured.series}</p>}
              <p className="mt-6 text-zinc-300 leading-relaxed line-clamp-[8]">{featured.description}</p>
              {featured.praise?.[0] && (
                <div className="mt-6 flex gap-3 items-start border-l-2 border-amber-500 pl-4">
                  <Star size={16} className="text-amber-500 mt-1 shrink-0" />
                  <p className="text-sm italic text-zinc-400">{featured.praise[0]}</p>
                </div>
              )}
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to={`/books/${featured.slug}`}
                  data-testid="featured-details-link"
                  className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 font-bold text-sm px-6 py-3 hover:bg-amber-400 transition-colors"
                >
                  Read more
                </Link>
                {featured.buy_links?.[0] && (
                  <a
                    href={featured.buy_links[0].url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="featured-buy-link"
                    className="inline-flex items-center gap-2 border border-zinc-700 text-sm px-6 py-3 hover:border-amber-500 hover:text-amber-500 transition-colors"
                  >
                    Buy on {featured.buy_links[0].label} <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* BOOK GRID */}
      <section data-testid="books-preview" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs tracking-accent uppercase text-amber-500 mb-3">The Library</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">All the books</h2>
            </div>
            <Link to="/books" className="hidden sm:inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-500 transition-colors">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {books.map((b, i) => (
              <BookCard key={b.id} book={b} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT SNIPPET */}
      {author && (
        <section data-testid="about-snippet" className="relative py-24 lg:py-32 border-y border-zinc-800 bg-zinc-900/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              <img src={author.portrait} alt={author.name} className="w-full aspect-[4/5] object-cover border border-zinc-800 grayscale hover:grayscale-0 transition-all duration-700" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-3"
            >
              <p className="text-xs tracking-accent uppercase text-amber-500 mb-4">About the author</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">{author.tagline}</h2>
              <p className="mt-6 text-zinc-300 leading-relaxed text-lg">{author.bio_short}</p>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-amber-500 text-sm tracking-wide hover:gap-3 transition-all">
                Read my full story <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      <Newsletter />
    </div>
  );
}
